// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomQuoteParameter } from "@ces/App/Handlers/CustomRfqRequestQuote/RfqRequestQuoteState";
import isApiError from "@insite/client-framework/Common/isApiError";
import {
    ApiParameter,
    del,
    doesNotHaveExpand,
    get,
    patch,
    post,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";
import {
    CreateQuoteApiParameter,
    GetQuoteApiParameter,
    GetQuotesApiParameter,
    UpdateQuoteApiParameter,
    UpdateQuoteLineApiParameter,
} from "@insite/client-framework/Services/QuoteService";
import { QuoteCollectionModel, QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";

export interface CustomQuoteModel extends QuoteModel {
    webQuoteNumber?: string;
    stCompanyName?: string;
    stAddress1?: string;
    stAddress2?: string;
    stCity?: string;
    stState?: string;
    stPostalCode?: string;
    customerPO?: string;
    lines?: QuoteLineModel[] | null;
    btAddress1?: string;
    btAddress2?: string;
    btCity?: string;
    btCompanyName?: string;
    btPostalCode?: string;
    btState?: string;
    productTotalDisplay?: string;
    taxDisplay?: string;
    quoteGrandTotalDisplay?: string;
    dateRequested?: string;
    allowResubmitExpiredQuote?: boolean;
}

export interface ReSubmittedQuote {
    quoteNumber: string;
}

export interface ReSubmittedQuoteMessage {
    message: string;
}

// XNG Changes: changing URL
const quotesUrl = "/api/ces/quote";
const requestQuotesUrl = "/api/ces/quote/requestquote";
const getCartIdUrl = "/api/ces/quote/getcartid";
const reSubmitQuoteUrl = "/api/ces/quote/resubmitexpiredquote";

export async function getQuotes(getQuotesParameter: GetQuotesApiParameter) {
    const quotes = await get<QuoteCollectionModel>(quotesUrl, {
        ...getQuotesParameter,
        timestamp: `${new Date().getTime()}`,
    } as ApiParameter);
    cleanQuoteCollection(quotes, getQuotesParameter);
    quotes.quotes?.forEach(cleanQuote);
    return quotes;
}

export interface CustomGetQuoteApiParameter extends ApiParameter {
    quoteNumber?: string;
}

export async function getQuote(parameter: CustomGetQuoteApiParameter) {
    const quote = await get<CustomQuoteModel>(`${quotesUrl}/detail`, {
        ...parameter,
        timestamp: `${new Date().getTime()}`,
    } as ApiParameter);
    cleanQuote(quote);
    return quote;
}

function cleanQuote(quote: CustomQuoteModel) {
    quote.orderDate = quote.orderDate ? new Date(quote.orderDate) : null;
    quote.expirationDate = quote.expirationDate ? new Date(quote.expirationDate) : null;
    quote.cartLines = quote.quoteLineCollection;
    quote.messageCollection?.forEach(message => {
        if (message.createdDate) {
            message.createdDate = new Date(message.createdDate);
        }
    });
    (quote as any).quoteLineCollection = quote.lines;
}

function cleanQuoteCollection(quoteCollection: QuoteCollectionModel, parameter?: { expand?: string[] }) {
    if (doesNotHaveExpand(parameter, "salesList")) {
        quoteCollection.salespersonList = null;
    }
}

export interface CustomCreateQuoteApiParameter extends CustomQuoteParameter {}

export async function createQuote(
    createQuoteParameter: CustomCreateQuoteApiParameter,
): Promise<ServiceResult<QuoteModel>> {
    try {
        const quote = await post<CustomCreateQuoteApiParameter, QuoteModel>(requestQuotesUrl, createQuoteParameter);
        return {
            successful: true,
            result: quote,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function reSubmitExpiredQuote(
    reSubmittedQuoteParameter: ReSubmittedQuote,
): Promise<ServiceResult<ReSubmittedQuoteMessage>> {
    try {
        const result = await post<ReSubmittedQuote, ReSubmittedQuoteMessage>(
            reSubmitQuoteUrl,
            reSubmittedQuoteParameter,
        );
        return {
            successful: true,
            result,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function getCartId(parameter: CustomGetQuoteApiParameter): Promise<ServiceResult<string>> {
    try {
        const result = await get<string>(`${getCartIdUrl}`, {
            ...parameter,
            timestamp: `${new Date().getTime()}`,
        } as ApiParameter);
        return {
            successful: true,
            result,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}
